import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button, TextField, Select, MenuItem, InputLabel, FormControl, Grid, Tooltip } from '@mui/material';
import { NotificationManager } from 'react-notifications';
import { GetAuditLog } from '../../services/AuditLogService';
import AuditLogTable from './AuditLogTable';
import CommonLoader from '../common/CommonLoader';
import { auditTypes, getAuditErrorMessage } from './auditLogUtils';

// ENB-577: admin-only audit history. One row per order / sample / result, latest activity first.
// The date range is optional and filters on the record's own date (for example date received).
// A row opens its full change history in a new tab.
const AuditLog = () => {
  const { type } = useParams();
  const typeInfo = auditTypes[type];

  const [loading, setLoading] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditCount, setAuditCount] = useState(0);
  const [dateMenuOpen, setDateMenuOpen] = useState(false);

  const [searchCriteria, setSearchCriteria] = useState({
    searchText: '',
    dateField: '',
    startDate: '',
    endDate: '',
    pageNumber: 0,
    pageSize: 25
  });

  const getAuditLog = async (criteria) => {
    try {
      setLoading(true);
      let response = await GetAuditLog(
        type,
        criteria.dateField,
        criteria.startDate,
        criteria.endDate,
        criteria.searchText,
        criteria.pageNumber,
        criteria.pageSize
      );
      setAuditData(response.results);
      setAuditCount(response.count);
    } catch (err) {
      NotificationManager.error(await getAuditErrorMessage(err), 'Error');
    } finally {
      setLoading(false);
    }
  };

  // Opens on the latest changes, no dates needed; changing the audit type (menu item) starts fresh.
  useEffect(() => {
    if (!typeInfo) return;
    const initialCriteria = {
      searchText: '',
      dateField: typeInfo.dateFields[0].value,
      startDate: '',
      endDate: '',
      pageNumber: 0,
      pageSize: 25
    };
    setAuditData(null);
    setSearchCriteria(initialCriteria);
    getAuditLog(initialCriteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // An order number search ignores the other filters, so an order's full history can always be found.
  const isOrderNumberSearch = /^\d+$/.test(searchCriteria.searchText.trim());

  const handleSearchSubmit = (e) => {
    e.preventDefault(); // Prevent page refresh

    if (!isOrderNumberSearch && searchCriteria.startDate && searchCriteria.endDate
      && searchCriteria.startDate > searchCriteria.endDate) {
      NotificationManager.error('From Date is after To Date');
      return;
    }

    const updatedSearchCriteria = { ...searchCriteria, pageNumber: 0 };
    setSearchCriteria(updatedSearchCriteria);
    getAuditLog(updatedSearchCriteria);
  };

  const onChangePageSettings = (pageNumber, pageSize) => {
    const updatedSearchCriteria = { ...searchCriteria, pageNumber: pageNumber, pageSize: pageSize };
    setSearchCriteria(updatedSearchCriteria);
    getAuditLog(updatedSearchCriteria);
  };

  if (!typeInfo) {
    return (
      <div className="vlp-component-container">
        <span className="title">Unknown audit type</span>
      </div>
    );
  }

  const selectedDateField = typeInfo.dateFields.find((f) => f.value === searchCriteria.dateField) || typeInfo.dateFields[0];

  return (
    <div className="vlp-component-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="title">{typeInfo.title}</span>
      </div>
      <hr />

      <div className="columns">
        <div className="column">
          <form onSubmit={handleSearchSubmit}>
            <Grid container spacing={2}>

              {/* Search Text */}
              <Grid item xs={12} sm={3}>
                <Tooltip
                  title="Enter an order number to see that order's full history, or text to search client name / status (orders), lab number / sample identity (samples), or test code / parameter (results). With no dates and no order number, the most recent changes are shown"
                  arrow
                  placement="top"
                >
                  <TextField
                    label="Search Criteria"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchCriteria.searchText}
                    onChange={(e) => setSearchCriteria({ ...searchCriteria, searchText: e.target.value })}
                  />
                </Tooltip>
              </Grid>

              {/* Which of the record's own dates the range applies to */}
              <Grid item xs={12} sm={2}>
                {/* The tooltip is hidden while the list is open, or it covers the options. */}
                <Tooltip
                  title={dateMenuOpen ? '' : "The date range applies to this date on the record (not to when the change was audited). Older changes have no audit date, and are found through these dates or an order number"}
                  arrow
                  placement="top"
                >
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Date</InputLabel>
                    <Select
                      value={selectedDateField.value}
                      label="Date"
                      open={dateMenuOpen}
                      onOpen={() => setDateMenuOpen(true)}
                      onClose={() => setDateMenuOpen(false)}
                      disabled={isOrderNumberSearch}
                      onChange={(e) => setSearchCriteria({ ...searchCriteria, dateField: e.target.value })}
                    >
                      {typeInfo.dateFields.map((f) => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>
              </Grid>

              {/* From Date */}
              <Grid item xs={12} sm={2}>
                <Tooltip title={`Optional. Filter by ${selectedDateField.label.toLowerCase()}`} arrow placement="top">
                  <TextField
                    label="From Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    size="small"
                    disabled={isOrderNumberSearch}
                    value={searchCriteria.startDate}
                    onChange={(e) => setSearchCriteria({ ...searchCriteria, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Tooltip>
              </Grid>

              {/* To Date */}
              <Grid item xs={12} sm={2}>
                <Tooltip title={`Optional. Filter by ${selectedDateField.label.toLowerCase()}`} arrow placement="top">
                  <TextField
                    label="To Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    size="small"
                    disabled={isOrderNumberSearch}
                    value={searchCriteria.endDate}
                    onChange={(e) => setSearchCriteria({ ...searchCriteria, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Tooltip>
              </Grid>

              {/* Search Button */}
              <Grid item xs={12} sm={2}>
                <Button type="submit" variant="contained" color="primary" size="medium">
                  Search
                </Button>
              </Grid>

            </Grid>
          </form>
        </div>
      </div>

      {auditData !== null && (
        <AuditLogTable
          type={type}
          dateLabel={selectedDateField.label}
          auditData={auditData}
          count={auditCount}
          page={searchCriteria.pageNumber}
          rowsPerPage={searchCriteria.pageSize}
          onChange={onChangePageSettings}
        />
      )}

      <CommonLoader loading={loading} />
    </div>
  );
};

export default AuditLog;
